import { OwnerBoardPage } from './pages/OwnerBoardPage';
import { Center, Loader } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { PASSWORD_CHANGE_EVENT } from './api';
import { ForcedPasswordChangePage } from './pages/ForcedPasswordChangePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAccountSwitchGuard, useMe } from './auth';
import { AppLayout } from './components/AppLayout';
import { ModuleGate } from './components/ModuleGate';
import { AuditPage } from './pages/AuditPage';
import { CasePage } from './pages/case/CasePage';
import { CasesPage } from './pages/CasesPage';
import { CatalogPage } from './pages/catalog/CatalogPage';
import { ContractPage } from './pages/contract/ContractPage';
import { FinancePage } from './pages/FinancePage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { OfferPage } from './pages/offer/OfferPage';
import { PublicOfferPage } from './pages/PublicOfferPage';
import { PublicEsignPage } from './pages/PublicEsignPage';
import { PublicPaymentPage } from './pages/PublicPaymentPage';
import { PublicPortalPage } from './pages/PublicPortalPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { SystemModulesPage } from './pages/SystemModulesPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { NumberingPage } from './pages/NumberingPage';
import { PartiesPage } from './pages/party/PartiesPage';
import { PartyPage } from './pages/party/PartyPage';
import { SitePage } from './pages/party/SitePage';
import { ProcessSettingsPage } from './pages/ProcessSettingsPage';
import { TasksInboxPage } from './pages/TasksInboxPage';
import { TaskTypesSettingsPage } from './pages/TaskTypesSettingsPage';
import { RolesPage } from './pages/RolesPage';
import { SearchPage } from './pages/SearchPage';
import { EmployeeCardPage } from './pages/employees/EmployeeCardPage';
import { EmployeesPage } from './pages/employees/EmployeesPage';
import { CalendarPage } from './pages/planning/CalendarPage';
import { MyCalendarPage } from './pages/MyCalendarPage';
import { PlanningPage } from './pages/planning/PlanningPage';
import { TeamsPage } from './pages/planning/TeamsPage';
import { InstallationsPage } from './pages/service/InstallationsPage';
import { ServicePage } from './pages/service/ServicePage';
import { FleetDashboardPage } from './pages/fleet/FleetDashboardPage';
import { VehiclesPage } from './pages/fleet/VehiclesPage';
import { VehiclePage } from './pages/fleet/VehiclePage';
import { FleetReportsPage } from './pages/fleet/ReportsPage';
import { FleetCostsPage } from './pages/fleet/CostsPage';
import { FleetImportPage } from './pages/fleet/ImportPage';
import { FleetSettingsPage } from './pages/fleet/FleetSettingsPage';
import { MyVehiclePage } from './pages/fleet/MyVehiclePage';
import { CountsPage } from './pages/warehouse/CountsPage';
import { DemandsPage } from './pages/warehouse/DemandsPage';
import { PackagesPage } from './pages/warehouse/PackagesPage';
import { OrdersPage } from './pages/warehouse/OrdersPage';
import { WarehouseDashboardPage } from './pages/warehouse/DashboardPage';
import { DocumentsPage } from './pages/warehouse/DocumentsPage';
import { StockPage } from './pages/warehouse/StockPage';
import { WarehouseSettingsPage } from './pages/warehouse/WarehouseSettingsPage';

/** Dawny adres strony (zakładki przeglądarki, stare linki) — przekierowanie z zachowaniem parametrów. */
function Moved({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={to + search} replace />;
}

export function App() {
  const location = useLocation();
  // strona klienta (link z ofertą) — bez logowania i bez układu aplikacji
  if (location.pathname.startsWith('/oferta/') || location.pathname.startsWith('/podpis/') || location.pathname.startsWith('/strefa/') || location.pathname.startsWith('/platnosc/')) {
    return (
      <Routes>
        <Route path="/oferta/:token" element={<PublicOfferPage />} />
        <Route path="/podpis/:token" element={<PublicEsignPage />} />
        <Route path="/strefa/:token" element={<PublicPortalPage />} />
        <Route path="/platnosc/:token" element={<PublicPaymentPage />} />
      </Routes>
    );
  }
  // link z e-maila „Nie pamiętasz hasła?” — bez logowania (także gdy ktoś jest zalogowany w tej przeglądarce)
  if (location.pathname === '/reset-hasla') return <ResetPasswordPage />;
  return <Authenticated />;
}

function Authenticated() {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  const qc = useQueryClient();
  useAccountSwitchGuard(me, isLoading);
  useEffect(() => {
    const reload = () => void qc.invalidateQueries({ queryKey: ['me'] });
    window.addEventListener(PASSWORD_CHANGE_EVENT, reload);
    return () => window.removeEventListener(PASSWORD_CHANGE_EVENT, reload);
  }, [qc]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  if (!me) {
    return (
      <Routes>
        <Route path="/logowanie" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/logowanie" replace state={{ from: location.pathname + location.search }} />} />
      </Routes>
    );
  }

  // administrator wymaga zmiany hasła — tylko ekran zmiany (jak logowanie), bez reszty CRM
  if (me.mustChangePassword) return <ForcedPasswordChangePage />;

  // każdy pracownik z kontem wchodzi do panelu (także bez modułów menu, np. monter) — co widzi, wynika z uprawnień (docs/06 §10)
  return (
    <Routes>
      <Route path="/logowanie" element={<Navigate to="/" replace />} />
      <Route element={<AppLayout me={me} />}>
        <Route index element={<DashboardPage me={me} />} />
        {/* strony modułów lewego menu — za uprawnieniem modul.<kod>; rekordy (sprawa, klient, oferta, umowa) wg uprawnień szczegółowych */}
        <Route path="sprawy" element={<ModuleGate me={me} module="sprawy"><CasesPage me={me} /></ModuleGate>} />
        <Route path="moje-sprawy" element={<ModuleGate me={me} module="moje_sprawy"><OwnerBoardPage me={me} /></ModuleGate>} />
        <Route path="zadania" element={<ModuleGate me={me} module="moje_zadania"><TasksInboxPage me={me} /></ModuleGate>} />
        <Route path="moj-kalendarz" element={<ModuleGate me={me} module="moj_kalendarz"><MyCalendarPage me={me} /></ModuleGate>} />
        <Route path="obieg" element={<Moved to="/zadania" />} />
        <Route path="sprawy/:id" element={<CasePage me={me} />} />
        <Route path="klienci" element={<ModuleGate me={me} module="klienci"><PartiesPage me={me} /></ModuleGate>} />
        <Route path="klienci/:id" element={<PartyPage me={me} />} />
        <Route path="lokalizacje/:id" element={<SitePage me={me} />} />
        <Route path="katalog" element={<ModuleGate me={me} module="katalog"><CatalogPage me={me} /></ModuleGate>} />
        <Route path="oferty/:id" element={<OfferPage me={me} />} />
        <Route path="umowy/:id" element={<ContractPage me={me} />} />
        <Route path="dokumenty/szablony" element={<ModuleGate me={me} module="szablony"><TemplatesPage me={me} /></ModuleGate>} />
        <Route path="finanse" element={<ModuleGate me={me} module="finanse"><FinancePage me={me} /></ModuleGate>} />
        <Route path="zestawienia" element={<ModuleGate me={me} module="zestawienia"><ReportsPage me={me} /></ModuleGate>} />
        <Route path="planowanie" element={<ModuleGate me={me} module="planowanie"><PlanningPage me={me} /></ModuleGate>} />
        <Route path="kalendarz" element={<ModuleGate me={me} module="kalendarz"><CalendarPage me={me} /></ModuleGate>} />
        <Route path="ekipy" element={<ModuleGate me={me} module="ekipy"><TeamsPage me={me} /></ModuleGate>} />
        <Route path="serwis" element={<ModuleGate me={me} module="serwis"><ServicePage me={me} /></ModuleGate>} />
        <Route path="instalacje" element={<ModuleGate me={me} module="instalacje"><InstallationsPage me={me} /></ModuleGate>} />
        {/* Flota (docs/19) — moduł opcjonalny; „Mój samochód” bez modułu (dostęp z przypisania pojazdu) */}
        <Route path="flota" element={<ModuleGate me={me} module="flota"><FleetDashboardPage me={me} /></ModuleGate>} />
        <Route path="flota/pojazdy" element={<ModuleGate me={me} module="flota"><VehiclesPage me={me} /></ModuleGate>} />
        <Route path="flota/pojazdy/:id" element={<ModuleGate me={me} module="flota"><VehiclePage me={me} /></ModuleGate>} />
        <Route path="flota/zgloszenia" element={<ModuleGate me={me} module="flota"><FleetReportsPage me={me} /></ModuleGate>} />
        <Route path="flota/koszty" element={<ModuleGate me={me} module="flota"><FleetCostsPage me={me} /></ModuleGate>} />
        <Route path="flota/import" element={<ModuleGate me={me} module="flota"><FleetImportPage me={me} /></ModuleGate>} />
        <Route path="flota/ustawienia" element={<ModuleGate me={me} module="flota"><FleetSettingsPage me={me} /></ModuleGate>} />
        <Route path="moj-samochod" element={<MyVehiclePage me={me} />} />
        <Route path="magazyn" element={<ModuleGate me={me} module="magazyn"><WarehouseHome /></ModuleGate>} />
        <Route path="magazyn/stany" element={<ModuleGate me={me} module="magazyn"><StockPage me={me} /></ModuleGate>} />
        <Route path="magazyn/dokumenty" element={<ModuleGate me={me} module="magazyn"><DocumentsPage me={me} /></ModuleGate>} />
        <Route path="magazyn/zapotrzebowania" element={<ModuleGate me={me} module="magazyn"><DemandsPage me={me} /></ModuleGate>} />
        <Route path="magazyn/pakiety" element={<ModuleGate me={me} module="magazyn"><PackagesPage me={me} /></ModuleGate>} />
        <Route path="magazyn/zamowienia" element={<ModuleGate me={me} module="magazyn"><OrdersPage me={me} /></ModuleGate>} />
        <Route path="magazyn/pulpit" element={<ModuleGate me={me} module="magazyn"><WarehouseDashboardPage me={me} /></ModuleGate>} />
        <Route path="magazyn/inwentaryzacja" element={<ModuleGate me={me} module="magazyn"><CountsPage me={me} /></ModuleGate>} />
        <Route path="ustawienia/magazyny" element={<ModuleGate me={me} module="magazyn"><WarehouseSettingsPage me={me} /></ModuleGate>} />
        <Route path="szukaj" element={<SearchPage me={me} />} />
        <Route path="ustawienia/firmy" element={<ModuleGate me={me} module="firmy"><CompaniesPage me={me} /></ModuleGate>} />
        <Route path="ustawienia/moduly" element={<ModuleGate me={me} module="firmy"><SystemModulesPage me={me} /></ModuleGate>} />
        <Route path="ustawienia/numeracja" element={<ModuleGate me={me} module="numeracja"><NumberingPage me={me} /></ModuleGate>} />
        <Route path="ustawienia/procesy" element={<ModuleGate me={me} module="procesy"><ProcessSettingsPage me={me} /></ModuleGate>} />
        <Route path="ustawienia/zadania" element={<ModuleGate me={me} module="obieg"><TaskTypesSettingsPage /></ModuleGate>} />
        <Route path="ustawienia/obieg" element={<Moved to="/ustawienia/zadania" />} />
        <Route path="ustawienia/czynnosci" element={<Navigate to="/ustawienia/procesy" replace />} />
        <Route path="pracownicy" element={<ModuleGate me={me} module="pracownicy"><EmployeesPage me={me} /></ModuleGate>} />
        <Route path="pracownicy/:id" element={<ModuleGate me={me} module="pracownicy"><EmployeeCardPage me={me} /></ModuleGate>} />
        <Route path="ustawienia/uzytkownicy" element={<Navigate to="/pracownicy" replace />} />
        <Route path="ustawienia/role" element={<ModuleGate me={me} module="role"><RolesPage me={me} /></ModuleGate>} />
        <Route path="historia" element={<ModuleGate me={me} module="historia"><AuditPage me={me} /></ModuleGate>} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/** /magazyn — wejście do modułu: pulpit; stare linki do Stanów z parametrami (np. ?filtr=) — do /magazyn/stany. */
function WarehouseHome() {
  const { search } = useLocation();
  return <Navigate to={search ? `/magazyn/stany${search}` : '/magazyn/pulpit'} replace />;
}
